/**
 * Nested markdown lists for the phone renderer. Consecutive list lines are
 * one block; indent and marker type decide nesting vs a new list.
 */

const LIST_ITEM = /^(\s*)([-*+]|\d+\.)\s+(.*)$/;

export type MdListItem = {
  ordered: boolean;
  content: string;
  children: MdListItem[];
};

export function matchListItem(
  line: string,
): { indent: number; ordered: boolean; content: string } | null {
  const m = LIST_ITEM.exec(line);
  if (!m) return null;
  return {
    indent: m[1]!.length,
    ordered: /^\d+\.$/.test(m[2]!),
    content: m[3]!,
  };
}

export function parseList(
  lines: string[],
  start: number,
): { items: MdListItem[]; next: number } {
  const flats: { indent: number; ordered: boolean; content: string }[] = [];
  let i = start;
  while (i < lines.length) {
    const item = matchListItem(lines[i] ?? '');
    if (!item) break;
    flats.push(item);
    i++;
  }
  return { items: nest(flats), next: i };
}

function nest(
  flats: { indent: number; ordered: boolean; content: string }[],
): MdListItem[] {
  const roots: MdListItem[] = [];
  const stack: { indent: number; item: MdListItem }[] = [];

  for (const flat of flats) {
    const node: MdListItem = {
      ordered: flat.ordered,
      content: flat.content,
      children: [],
    };
    while (stack.length > 0 && stack[stack.length - 1]!.indent >= flat.indent) {
      stack.pop();
    }
    const parent = stack[stack.length - 1];
    if (parent) parent.item.children.push(node);
    else roots.push(node);
    stack.push({ indent: flat.indent, item: node });
  }

  return roots;
}
